import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import {
  AutoLinkPlugin,
  createLinkMatcherWithRegExp,
} from '@lexical/react/LexicalAutoLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isTextNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type DOMExportOutputMap,
  EditorState,
  isHTMLElement,
  type Klass,
  type LexicalEditor,
  type LexicalNode,
  ParagraphNode,
  TextNode,
} from 'lexical';
import { useMemo } from 'react';
import ExampleTheme from './plugins/ExampleTheme';
import './plugins/styles.css';
import ToolbarPlugin from './plugins/ToolbarPlugin';

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
  showToolbar?: boolean;
}

interface SerializedEditorState {
  root: {
    children: Array<{
      type: string;
      children?: Array<{
        type: string;
        text?: string;
        style?: string;
        format?: number;
        [key: string]: any;
      }>;
      format?: string;
      indent?: number;
      direction?: string | null;
      [key: string]: any;
    }>;
    direction?: string | null;
    format: string;
    indent?: number;
  };
}

const URL_REGEX =
  /((https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z0-9.-]+(\.[a-zA-Z]{2,})?(\/\S*)?)/;
const EMAIL_REGEX = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;

const MATCHERS = [
  createLinkMatcherWithRegExp(URL_REGEX, (text) =>
    text.startsWith('http') ? text : `https://${text}`
  ),
  createLinkMatcherWithRegExp(EMAIL_REGEX, (text) => `mailto:${text}`),
];

const removeStylesExportDOM = (
  editor: LexicalEditor,
  target: LexicalNode
): DOMExportOutput => {
  const output = target.exportDOM(editor);
  if (output && isHTMLElement(output.element)) {
    for (const el of [
      output.element,
      ...Array.from(output.element.querySelectorAll('[style],[class]')),
    ]) {
      el.removeAttribute('class');
      el.removeAttribute('style');
    }
  }
  return output;
};

const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [ParagraphNode, removeStylesExportDOM],
  [TextNode, removeStylesExportDOM],
]);

const parseAllowedColor = (input: string): string => {
  const hex = /^#[0-9A-Fa-f]{6}$/;
  const rgb = /^rgb\(\d+, \d+, \d+\)$/;
  if (hex.test(input) || rgb.test(input)) {
    return input;
  }
  return '';
};

const parseAllowedFontSize = (input: string): string => {
  const match = /^(\d+)(px|em|rem)$/.exec(input);
  if (match) {
    const size = parseInt(match[1], 10);
    if (size >= 8 && size <= 72) {
      return input;
    }
  }
  return '';
};

const getExtraStyles = (element: HTMLElement): string => {
  let extraStyles = '';
  const fontSize = parseAllowedFontSize(element.style.fontSize);
  const backgroundColor = parseAllowedColor(element.style.backgroundColor);
  const color = parseAllowedColor(element.style.color);
  if (fontSize !== '' && fontSize !== '15px') {
    extraStyles += `font-size: ${fontSize};`;
  }
  if (backgroundColor !== '' && backgroundColor !== 'rgb(255, 255, 255)') {
    extraStyles += `background-color: ${backgroundColor};`;
  }
  if (color !== '' && color !== 'rgb(0, 0, 0)') {
    extraStyles += `color: ${color};`;
  }
  return extraStyles;
};

const constructImportMap = (): DOMConversionMap => {
  const importMap: DOMConversionMap = {};

  TextNode.getType();
  const importDOMFn = TextNode.importDOM;

  for (const [tag, fn] of Object.entries(
    importDOMFn ? importDOMFn() || {} : {}
  )) {
    importMap[tag] = (importNode) => {
      const importer = fn(importNode);
      if (!importer) {
        return null;
      }
      return {
        ...importer,
        conversion: (element) => {
          const output = importer.conversion(element);
          if (
            output === null ||
            output.forChild === undefined ||
            output.after !== undefined ||
            output.node !== null
          ) {
            return output;
          }
          const extraStyles = getExtraStyles(element);
          if (extraStyles) {
            const { forChild } = output;
            return {
              ...output,
              forChild: (child, parent) => {
                const textNode = forChild(child, parent);
                if ($isTextNode(textNode)) {
                  textNode.setStyle(textNode.getStyle() + extraStyles);
                }
                return textNode;
              },
            };
          }
          return output;
        },
      };
    };
  }

  return importMap;
};

/**
 * Serialize editor state to JSON.
 */
export function serializeEditorState(editorState: EditorState): string {
  try {
    const json = editorState.toJSON();
    return JSON.stringify(json);
  } catch (error) {
    console.error('Error serializing editor state:', error);
    return '';
  }
}

/**
 * DDeserialize a JSON string into editor state.
 */
function createEditorStateFromJSON(jsonString: string) {
  if (!jsonString) return undefined;

  return (editor: LexicalEditor) => {
    try {
      const parsedState = JSON.parse(jsonString) as SerializedEditorState;
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        // Reconstruct the editor state from the parsed JSON
        if (
          parsedState.root?.children &&
          Array.isArray(parsedState.root.children)
        ) {
          parsedState.root.children.forEach((child: any) => {
            if (child.type === 'paragraph') {
              const paragraph = $createParagraphNode();
              // Apply paragraph properties
              if (child.format) {
                paragraph.setFormat(child.format);
              }
              if (child.indent !== undefined && child.indent !== null) {
                paragraph.setIndent(child.indent);
              }
              // Add text children
              if (child.children && Array.isArray(child.children)) {
                child.children.forEach((textChild: any) => {
                  if (
                    textChild.type === 'text' &&
                    textChild.text !== undefined
                  ) {
                    const textNode = $createTextNode(textChild.text);
                    // Apply styles
                    if (textChild.style) {
                      textNode.setStyle(textChild.style);
                    }
                    // Apply format (bold, italic, etc.)
                    if (
                      textChild.format !== undefined &&
                      typeof textChild.format === 'number'
                    ) {
                      textNode.setFormat(textChild.format);
                    }
                    paragraph.append(textNode);
                  }
                });
              }
              if (paragraph.getChildren().length > 0 || !root.getFirstChild()) {
                root.append(paragraph);
              }
            }
          });
        }
      });
    } catch (error) {
      console.error('Error deserializing editor state:', error);
      // Fallback: create an empty editor state if deserialization fails
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const emptyParagraph = $createParagraphNode();
        root.append(emptyParagraph);
      });
    }
  };
}

/**
 * Initialize the editor state from the stored value
 */
function initialEditorStateFromValue(value: string) {
  if (!value || value.trim() === '') return undefined;
  try {
    const parsed = JSON.parse(value);
    if (parsed.root && parsed.root.children) {
      return createEditorStateFromJSON(value);
    }
  } catch (e) {
    console.log('Error parsing value, treating as plain text:', value);
  }

  return (editor: LexicalEditor) => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(value));
      root.append(paragraph);
    });
  };
}

export function RichTextEditor({
  value,
  onChange,
  disabled,
  placeholder = 'Veuillez remplir les informations demandées.',
  showToolbar = false,
}: RichTextEditorProps) {
  const editorConfig = useMemo(
    () => ({
      html: {
        export: exportMap,
        import: constructImportMap(),
      },
      namespace: 'HighImpactEditor',
      nodes: [
        ParagraphNode,
        TextNode,
        ListNode,
        ListItemNode,
        LinkNode,
        AutoLinkNode,
      ],
      onError: (error: Error) => console.error('Lexical error:', error),
      theme: ExampleTheme,
      editable: !disabled,
      editorState: initialEditorStateFromValue(value),
    }),
    [disabled, value]
  );

  const handleEditorChange = (editorState: EditorState) => {
    editorState.read(() => {
      const serialized = serializeEditorState(editorState);
      onChange(serialized);
    });
  };

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="editor-container">
        {showToolbar && !disabled && <ToolbarPlugin />}
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="editor-input"
                aria-placeholder={placeholder}
                placeholder={
                  <div className="editor-placeholder">{placeholder}</div>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ListPlugin />
          <LinkPlugin />
          <AutoLinkPlugin matchers={MATCHERS} />
          <OnChangePlugin onChange={handleEditorChange} />
        </div>
      </div>
    </LexicalComposer>
  );
}
